import { ValidationError } from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'

import { Aliased, Identified } from './interfaces'

/**
 * Used to reference something that is both {@link Identified} and
 * {@link Aliased}
 *
 * __NOTE:__ when you have access to both the {@link Identified.id} and the
 * {@link Aliased.alias}, we recommend passing both since this allows the
 * underlying system to use whichever is optimal in the given context
 */
export type FlexibleIdentifier<TId extends Identified.Id = Identified.Id> =
  AtLeastOneOf<Identified<TId> & Aliased>
export namespace FlexibleIdentifier {
  export const isFlexibleIdentifier = TypeChecker.Factory.union(
    Identified.isIdentified,
    Aliased.isAliased,
  )

  /**
   * Throws a {@link ValidationError} if neither {@link FlexibleIdentifier.id}
   * nor {@link FlexibleIdentifier.alias} are defined on the given
   * {@link FlexibleIdentifier}
   *
   * Should only be necessary in contexts where you don't trust the input to be
   * compliant with the {@link FlexibleIdentifier} type, as that type requires
   * at least one property to be defined through {@link AtLeastOneOf}
   */
  export function validate<
    TIdentifier extends FlexibleIdentifier<any>,
    TNotIdentifier,
  >(
    identifier: TIdentifier | TNotIdentifier,
    {
      type = 'identifier',
    }: Pick<ValidationError.constructor.Options, 'type'> = {},
  ): asserts identifier is TIdentifier {
    const { alias, id } = identifier as Partial<FlexibleIdentifier>
    if (!alias && !id) {
      throw new ValidationError({
        reason: "Must contain 'id' or 'alias'",
        type,
        value: identifier,
      })
    }
  }
}
